/**
 * diasResumen.js — el resumen del panel «Días» (rediseño, Fase 2). Puro, sin React ni red.
 *
 * Cuenta con la MISMA regla que el servidor (generarDiasDisponibles en
 * campanasDiasDisponibles.js): un día del rango está disponible si no está excluido a mano, ni es
 * fin de semana con los fines de semana excluidos, ni es festivo. Los festivos no llegan a esta
 * pantalla: aquí se cuenta todo MENOS los festivos, y el número de festivos que afectan sale de
 * restarle los días que da el servidor (preview-capacidad), que sí los descuenta.
 */

function aUTC(s) {
  const [y, m, d] = String(s).split('-').map(Number);
  return Date.UTC(y, m - 1, d);
}
function aTexto(ms) {
  return new Date(ms).toISOString().slice(0, 10);
}

/**
 * @returns {{ totales: number, findes: number, manuales: number, disponiblesAntesDeFestivos: number,
 *            excluidos: Array<{ fecha: string, enRango: boolean, cuenta: boolean }> }}
 *   `findes`: fines de semana descontados (0 si se permiten). `manuales`: excluidos a mano que
 *   cuentan (en el rango y que no fueran ya fin de semana descontado). `excluidos`: la lista entera
 *   de excluidos a mano, ordenada, diciendo si está en el rango y si cuenta.
 */
export function resumenDias({ fecha_inicio, fecha_limite, excluir_fines_de_semana = true, excluidos_manuales = [] }) {
  const lista = [...new Set(excluidos_manuales || [])].sort();
  const vacio = {
    totales: 0, findes: 0, manuales: 0, disponiblesAntesDeFestivos: 0,
    excluidos: lista.map((fecha) => ({ fecha, enRango: false, cuenta: false })),
  };
  if (!fecha_inicio || !fecha_limite || fecha_limite < fecha_inicio) return vacio;

  const manualSet = new Set(lista);
  const cuentan = new Set();
  let totales = 0; let findes = 0; let manuales = 0;
  for (let t = aUTC(fecha_inicio), fin = aUTC(fecha_limite); t <= fin; t += 86400000) {
    totales += 1;
    const dow = new Date(t).getUTCDay();
    const esFinde = dow === 0 || dow === 6;
    const fecha = aTexto(t);
    if (excluir_fines_de_semana && esFinde) { findes += 1; continue; }
    if (manualSet.has(fecha)) { manuales += 1; cuentan.add(fecha); }
  }
  return {
    totales,
    findes,
    manuales,
    disponiblesAntesDeFestivos: totales - findes - manuales,
    excluidos: lista.map((fecha) => ({
      fecha,
      enRango: fecha >= fecha_inicio && fecha <= fecha_limite,
      cuenta: cuentan.has(fecha),
    })),
  };
}

/**
 * Festivos que quitan días: lo que queda antes de festivos menos lo que da el servidor. null si no
 * hay dato del servidor o si la resta no tiene sentido (negativa: las dos cuentas no cuadran, y
 * entonces no se enseña un número inventado).
 */
export function festivosQueAfectan(disponiblesAntesDeFestivos, diasServidor) {
  if (!Number.isInteger(diasServidor) || !Number.isInteger(disponiblesAntesDeFestivos)) return null;
  const n = disponiblesAntesDeFestivos - diasServidor;
  return n >= 0 ? n : null;
}

const DIA_CORTO = ['', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'];
const hhmm = (t) => String(t || '').slice(0, 5);
const minutos = (t) => { const [h, m] = String(t || '').split(':').map(Number); return (h || 0) * 60 + (m || 0); };

/**
 * Las franjas por día de la semana, en orden (lunes primero) y con los tramos ordenados por hora:
 * [{ dia: 1, corto: 'Lun', tramos: ['09:00–13:00', '15:00–17:00'] }]. Los días sin franja no salen.
 */
export function resumenFranjas(franjas) {
  const porDia = {};
  (Array.isArray(franjas) ? franjas : []).forEach((f) => {
    if (!f || !(f.dia_semana >= 1 && f.dia_semana <= 7)) return;
    (porDia[f.dia_semana] = porDia[f.dia_semana] || []).push(f);
  });
  return Object.keys(porDia).map(Number).sort((a, b) => a - b).map((dia) => ({
    dia,
    corto: DIA_CORTO[dia],
    tramos: porDia[dia]
      .slice()
      .sort((a, b) => minutos(a.hora_inicio) - minutos(b.hora_inicio))
      .map((f) => `${hhmm(f.hora_inicio)}–${hhmm(f.hora_fin)}`),
  }));
}

/** Los meses a la vista: el actual y el siguiente, si el siguiente no pasa del último. */
export function mesesAVista(actual, ultimo) {
  if (!actual) return [];
  const sig = actual.month === 12 ? { year: actual.year + 1, month: 1 } : { year: actual.year, month: actual.month + 1 };
  const n = (m) => m.year * 12 + m.month;
  return ultimo && n(sig) <= n(ultimo) ? [actual, sig] : [actual];
}
